import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  Keyboard,
  Mic,
  MicOff,
  Phone,
  PhoneCall,
  PhoneIncoming,
  PhoneOff,
  TriangleAlert,
} from 'lucide-react';
import { clearCallStatus, useCallStatus, type CallStatusSnapshot } from './callStatus.js';

const BAR_HEIGHT = 44;

export function useCallStatusBarVisible() {
  const status = useCallStatus();
  return isVisibleStatus(status);
}

export function CallStatusBar({
  sidebarWidth,
  topOffset,
}: {
  sidebarWidth: number;
  topOffset: number;
}) {
  const status = useCallStatus();
  const [showEnded, setShowEnded] = useState(false);
  const visible = isVisibleStatus(status) || (status.state === 'ended' && showEnded);
  const duration = useCallDuration(status.startedAt, status.state === 'in-call');

  useEffect(() => {
    if (status.state !== 'ended') {
      setShowEnded(false);
      return;
    }
    setShowEnded(true);
    const timeout = window.setTimeout(() => {
      setShowEnded(false);
      clearCallStatus();
    }, 8000);
    return () => window.clearTimeout(timeout);
  }, [status.state, status.updatedAt]);

  if (!visible) return null;

  const tone = getTone(status.state);
  const Icon = getIcon(status.state);
  const label = getLabel(status);
  const detail = getDetail(status);

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: topOffset > 0
          ? `calc(var(--topbar-height) + ${topOffset}px)`
          : 'var(--topbar-height)',
        left: sidebarWidth,
        right: 0,
        height: BAR_HEIGHT,
        zIndex: 'var(--z-call-status)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '0 24px',
        borderBottom: '1px solid var(--color-border)',
        background: tone.background,
        color: tone.color,
        boxShadow: '0 8px 16px rgba(15, 23, 42, 0.06)',
        transition: 'left 250ms ease',
        overflowX: 'auto',
      }}
    >
      <span
        aria-hidden
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 28,
          borderRadius: '50%',
          background: tone.iconBackground,
          color: tone.iconColor,
          flex: '0 0 auto',
        }}
      >
        <Icon size={16} />
      </span>
      <div style={{ minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 8, flex: 1 }}>
        <strong style={{ whiteSpace: 'nowrap' }}>{label}</strong>
        {detail ? (
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: tone.detailColor,
            }}
          >
            {detail}
          </span>
        ) : null}
        {duration ? (
          <span style={{ color: tone.detailColor, fontVariantNumeric: 'tabular-nums' }}>
            {duration}
          </span>
        ) : null}
      </div>
      {status.controls.canMute ? (
        <button
          type="button"
          className="btn btn-sm btn-secondary"
          onClick={status.controls.toggleMute ?? undefined}
          title={status.muted ? 'Unmute call' : 'Mute call'}
        >
          {status.muted ? <MicOff size={13} style={{ marginRight: 4 }} /> : <Mic size={13} style={{ marginRight: 4 }} />}
          {status.muted ? 'Muted' : 'Mute'}
        </button>
      ) : null}
      {status.controls.canSendDigits ? (
        <button
          type="button"
          className="btn btn-sm btn-ghost"
          onClick={status.controls.openDialer ?? undefined}
          title="Open keypad"
        >
          <Keyboard size={13} style={{ marginRight: 4 }} /> Keypad
        </button>
      ) : null}
      {status.controls.canAccept ? (
        <button
          type="button"
          className="btn btn-sm"
          onClick={status.controls.accept ?? undefined}
          title="Answer incoming call"
          style={{
            whiteSpace: 'nowrap',
            background: '#2D9D6E',
            color: '#fff',
            border: '1px solid #2D9D6E',
            boxShadow: '0 4px 10px rgba(45, 157, 110, 0.33)',
          }}
        >
          <Phone size={13} style={{ marginRight: 4 }} /> Answer
        </button>
      ) : null}
      {status.controls.canReject ? (
        <button
          type="button"
          className="btn btn-sm btn-secondary"
          onClick={status.controls.rejectCall ?? undefined}
          title="Reject the dashboard ring and let the caller continue to voicemail timeout"
          style={{ whiteSpace: 'nowrap' }}
        >
          <PhoneOff size={13} style={{ marginRight: 4 }} /> Reject
        </button>
      ) : null}
      {status.controls.canSendToVoicemail ? (
        <button
          type="button"
          className="btn btn-sm btn-danger"
          onClick={status.controls.sendToVoicemail ?? undefined}
          title="Send caller to voicemail now"
          style={{ whiteSpace: 'nowrap' }}
        >
          <PhoneIncoming size={13} style={{ marginRight: 4 }} /> Send to voicemail
        </button>
      ) : null}
      {status.controls.canHangUp ? (
        <button
          type="button"
          className="btn btn-sm btn-danger"
          onClick={status.controls.hangUp ?? undefined}
          title="Hang up call"
        >
          <PhoneOff size={13} style={{ marginRight: 4 }} /> Hang up
        </button>
      ) : null}
      {/* Manual "expand the full cockpit" affordance — the cockpit
          no longer auto-pops on every ring/call so the agent stays
          focused on whatever they were doing. They click this when
          they actually want the rich UI (notes, outcome tags,
          keypad, transfer, etc). */}
      {status.controls.openDialer ? (
        <button
          type="button"
          className="btn btn-sm btn-ghost"
          onClick={status.controls.openDialer ?? undefined}
          title="Open the full dialer cockpit"
          style={{ whiteSpace: 'nowrap' }}
        >
          <PhoneCall size={13} style={{ marginRight: 4 }} /> Open dialer
        </button>
      ) : null}
      <Link to="/comms" className="btn btn-sm btn-secondary">
        Phone & Text
      </Link>
      <Link to="/voicemail" className="btn btn-sm btn-ghost">
        Voicemail
      </Link>
    </div>
  );
}

export const callStatusBarHeight = BAR_HEIGHT;

function isVisibleStatus(status: CallStatusSnapshot): boolean {
  return status.state === 'connecting'
    || status.state === 'ringing'
    || status.state === 'in-call'
    || status.state === 'error';
}

function getLabel(status: CallStatusSnapshot): string {
  if (status.state === 'ringing') return 'Incoming call';
  if (status.state === 'connecting') return 'Calling';
  if (status.state === 'in-call') return 'Call live';
  if (status.state === 'ended') return 'Call ended';
  if (status.state === 'error') return 'Call issue';
  return 'Phone';
}

function getDetail(status: CallStatusSnapshot): string | null {
  if (status.message) return status.message;
  // Inbound legs carry the server's owner-scoped identity ("Jane Doe ·
  // (928) 555-1234"); prefer it over the bare number.
  if (status.direction === 'inbound' && status.peerDisplay) return `From ${status.peerDisplay}`;
  if (status.peerNumber) {
    const prefix = status.direction === 'inbound' ? 'From' : 'To';
    return `${prefix} ${status.peerNumber}`;
  }
  return null;
}

function useCallDuration(startedAt: number | null, active: boolean): string | null {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!active || !startedAt) return undefined;
    setNow(Date.now());
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [active, startedAt]);

  if (!active || !startedAt) return null;
  const totalSeconds = Math.max(0, Math.floor((now - startedAt) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function getIcon(state: CallStatusSnapshot['state']) {
  if (state === 'ringing') return PhoneIncoming;
  if (state === 'in-call') return PhoneCall;
  if (state === 'ended') return PhoneOff;
  if (state === 'error') return TriangleAlert;
  return Phone;
}

function getTone(state: CallStatusSnapshot['state']) {
  if (state === 'in-call') {
    return {
      background: 'var(--success-bg)',
      color: 'var(--success-text)',
      detailColor: 'var(--success-text)',
      iconBackground: 'rgba(5, 150, 105, 0.16)',
      iconColor: 'var(--success-text)',
    };
  }
  if (state === 'error') {
    return {
      background: 'var(--error-bg)',
      color: 'var(--error-text)',
      detailColor: 'var(--error-text)',
      iconBackground: 'rgba(185, 28, 28, 0.14)',
      iconColor: 'var(--error-text)',
    };
  }
  if (state === 'ended') {
    return {
      background: 'var(--bg-card)',
      color: 'var(--text)',
      detailColor: 'var(--text-muted)',
      iconBackground: 'var(--bg-subtle)',
      iconColor: 'var(--text-muted)',
    };
  }
  return {
    background: 'var(--warning-bg)',
    color: 'var(--warning-text)',
    detailColor: 'var(--warning-text)',
    iconBackground: 'rgba(217, 119, 6, 0.16)',
    iconColor: 'var(--warning-text)',
  };
}
